/**
 * Opções ECharts dos gráficos técnicos do relatório.
 *
 * O destino é um PDF impresso em fundo branco: só o modo claro, sem camada de
 * hover (o SVG é estático). Toda série tem, ao lado, uma tabela com os mesmos
 * números — é ela que garante a leitura sem depender de cor.
 *
 * Paleta: os 3 primeiros slots categóricos da paleta de referência, validados
 * em todos os pares (scripts/validate_palette.js --pairs all). Sequencial em
 * azul. Texto sempre em tinta neutra, nunca na cor da série.
 */

import type { EChartsCoreOption } from "echarts/core";
import type {
  ResourceHistogramBin,
  RestrictionBreakdownResponse,
} from "../../../services/api";

export const CHART_COLORS = {
  series1: "#2a78d6",
  series2: "#eb6834",
  series3: "#1baf7a",
  textPrimary: "#0b0b0b",
  textSecondary: "#52514e",
  grid: "#e5e4e0",
  axis: "#b8b7b2",
  surface: "#ffffff",
} as const;

const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
const BAR_RADIUS_HORIZONTAL: [number, number, number, number] = [0, 4, 4, 0];

const nf = (value: number, digits = 1) =>
  value.toLocaleString("pt-BR", { maximumFractionDigits: digits });

const axisLabel = { color: CHART_COLORS.textSecondary, fontSize: 10 };
const axisName = { color: CHART_COLORS.textSecondary, fontSize: 10 };
const splitLine = { lineStyle: { color: CHART_COLORS.grid, width: 1 } };
const axisLine = { lineStyle: { color: CHART_COLORS.axis } };
/** Nome do eixo x centralizado abaixo dele — na ponta, ele é cortado. */
const BOTTOM_AXIS_NAME = { nameLocation: "middle", nameGap: 22 };

function valueAxis(name: string, extra: Record<string, unknown> = {}) {
  return {
    type: "value",
    name,
    nameTextStyle: axisName,
    axisLabel: { ...axisLabel, formatter: (v: number) => nf(v, 2) },
    splitLine,
    ...extra,
  };
}

function categoryAxis(data: string[], extra: Record<string, unknown> = {}) {
  return {
    type: "category",
    data,
    axisLabel: { ...axisLabel, interval: 0 },
    axisLine,
    axisTick: { show: false },
    ...extra,
  };
}

/**
 * Waterfall do Memorial de Cálculo da Área Propícia: área total → cada
 * restrição (atribuição exclusiva) → não atribuído → área propícia, em % da
 * área. As barras de restrição "flutuam" sobre uma base invisível.
 */
export function areaWaterfallOption(
  breakdown: RestrictionBreakdownResponse
): EChartsCoreOption {
  const steps = breakdown.restrictions
    .filter((r) => r.available && r.percentage > 0)
    .map((r) => ({ label: r.label, value: r.percentage }));
  if (breakdown.unattributed_percentage > 0) {
    steps.push({ label: "Não atribuído", value: breakdown.unattributed_percentage });
  }

  const categories = ["Área total", ...steps.map((s) => s.label), "Área propícia"];
  const base: number[] = [0];
  const excluded: Array<number | "-"> = ["-"];
  const totals: Array<number | "-"> = [100];
  let running = 100;
  for (const step of steps) {
    running -= step.value;
    base.push(Math.max(running, 0));
    excluded.push(step.value);
    totals.push("-");
  }
  base.push(0);
  excluded.push("-");
  totals.push(breakdown.propitious_percentage);

  return {
    grid: { left: 150, right: 48, top: 28, bottom: 36 },
    legend: {
      top: 0,
      right: 0,
      textStyle: { color: CHART_COLORS.textSecondary, fontSize: 10 },
      data: ["Área", "Excluído pela restrição"],
    },
    xAxis: valueAxis("% da área", { max: 100, ...BOTTOM_AXIS_NAME }),
    yAxis: categoryAxis(categories, { inverse: true }),
    series: [
      {
        name: "base",
        type: "bar",
        stack: "w",
        silent: true,
        itemStyle: { color: "transparent" },
        data: base,
      },
      {
        name: "Excluído pela restrição",
        type: "bar",
        stack: "w",
        barWidth: 12,
        itemStyle: { color: CHART_COLORS.series2, borderRadius: 4 },
        label: {
          show: true,
          position: "right",
          color: CHART_COLORS.textPrimary,
          fontSize: 10,
          formatter: (p: { value: number }) => `−${nf(p.value)}%`,
        },
        data: excluded,
      },
      {
        name: "Área",
        type: "bar",
        stack: "w",
        barWidth: 12,
        itemStyle: { color: CHART_COLORS.series1, borderRadius: BAR_RADIUS_HORIZONTAL },
        label: {
          show: true,
          position: "right",
          color: CHART_COLORS.textPrimary,
          fontSize: 10,
          fontWeight: "bold",
          formatter: (p: { value: number }) => `${nf(p.value)}%`,
        },
        data: totals,
      },
    ],
  };
}

/** Energia anual por nível de projeção — a cascata do Teórico ao último nível da energia. */
export function cascadeOption(
  rows: Array<{ label: string; energyGwh: number | null }>,
  formatEnergy: (gwh: number) => string
): EChartsCoreOption {
  return {
    grid: { left: 56, right: 16, top: 28, bottom: 28 },
    xAxis: categoryAxis(rows.map((r) => r.label)),
    yAxis: valueAxis("GWh/ano"),
    series: [
      {
        type: "bar",
        barWidth: 36,
        itemStyle: { color: CHART_COLORS.series1, borderRadius: BAR_RADIUS },
        label: {
          show: true,
          position: "top",
          color: CHART_COLORS.textPrimary,
          fontSize: 10,
          formatter: (p: { value: number }) => formatEnergy(p.value),
        },
        data: rows.map((r) => (r.energyGwh == null ? "-" : r.energyGwh)),
      },
    ],
  };
}

/**
 * Histograma de uma variável na área, com a média de Pernambuco como linha de
 * referência quando disponível.
 */
export function histogramOption(
  bins: ResourceHistogramBin[],
  unit: string,
  reference?: { label: string; value: number } | null
): EChartsCoreOption {
  const digits = bins.length && bins[bins.length - 1].end - bins[0].start < 5 ? 2 : 1;
  const centers = bins.map((b) => (b.start + b.end) / 2);
  const data = bins.map((b, i) => [centers[i], b.percentage]);
  const width = bins.length ? bins[0].end - bins[0].start : 1;

  const series: Record<string, unknown>[] = [
    {
      type: "bar",
      barWidth: "92%",
      itemStyle: { color: CHART_COLORS.series1, borderRadius: BAR_RADIUS },
      data,
      markLine: reference
        ? {
            silent: true,
            symbol: "none",
            lineStyle: { color: CHART_COLORS.textPrimary, type: "dashed", width: 1.5 },
            label: {
              color: CHART_COLORS.textPrimary,
              fontSize: 10,
              formatter: `${reference.label}: ${nf(reference.value, digits)}`,
            },
            data: [{ xAxis: reference.value }],
          }
        : undefined,
    },
  ];

  return {
    grid: { left: 44, right: 24, top: 24, bottom: 34 },
    xAxis: {
      type: "value",
      name: unit,
      nameLocation: "middle",
      nameGap: 22,
      nameTextStyle: axisName,
      min: bins.length ? bins[0].start - width / 2 : undefined,
      max: bins.length ? bins[bins.length - 1].end + width / 2 : undefined,
      axisLabel: { ...axisLabel, formatter: (v: number) => nf(v, digits) },
      axisLine,
      splitLine: { show: false },
    },
    yAxis: valueAxis("% da área"),
    series,
  };
}

/** Densidade de Weibull f(v; A, k), em % por m/s. */
export function weibullPdfPercent(v: number, a: number, k: number): number {
  if (v <= 0 || a <= 0 || k <= 0) return 0;
  return (k / a) * Math.pow(v / a, k - 1) * Math.exp(-Math.pow(v / a, k)) * 100;
}

/**
 * Curva de Weibull com os parâmetros médios da área: a fração do tempo em
 * cada velocidade (% por m/s). É a distribuição temporal do vento — diferente
 * do histograma de velocidade média, que é espacial (entre pixels).
 */
export function weibullOption(a: number, k: number): EChartsCoreOption {
  const maxV = Math.max(20, Math.ceil(a * 2.5));
  const curve: Array<[number, number]> = [];
  for (let v = 0; v <= maxV; v += 0.25) curve.push([v, weibullPdfPercent(v, a, k)]);

  return {
    grid: { left: 44, right: 16, top: 24, bottom: 34 },
    xAxis: {
      type: "value",
      name: "Velocidade do vento (m/s)",
      nameLocation: "middle",
      nameGap: 22,
      nameTextStyle: axisName,
      max: maxV,
      axisLabel,
      axisLine,
      splitLine: { show: false },
    },
    yAxis: valueAxis("% do tempo por m/s"),
    series: [
      {
        type: "line",
        smooth: true,
        showSymbol: false,
        lineStyle: { color: CHART_COLORS.series1, width: 2 },
        areaStyle: { color: CHART_COLORS.series1, opacity: 0.12 },
        data: curve,
      },
    ],
  };
}

const SECTOR_LABEL: Record<number, string> = {
  0: "N",
  30: "30°",
  60: "60°",
  90: "L",
  120: "120°",
  150: "150°",
  180: "S",
  210: "210°",
  240: "240°",
  270: "O",
  300: "300°",
  330: "330°",
};

/** Rosa dos ventos: frequência média por setor de 30°, a 100 m. */
export function windRoseOption(
  sectors: Array<{ direction_deg: number; frequency_percentage: number }>
): EChartsCoreOption {
  const ordered = [...sectors].sort((a, b) => a.direction_deg - b.direction_deg);
  return {
    polar: { radius: ["8%", "72%"], center: ["50%", "54%"] },
    angleAxis: {
      type: "category",
      data: ordered.map((s) => SECTOR_LABEL[s.direction_deg] ?? `${s.direction_deg}°`),
      startAngle: 90 + 15,
      clockwise: true,
      axisLabel: { color: CHART_COLORS.textSecondary, fontSize: 10 },
      axisLine,
      axisTick: { show: false },
    },
    radiusAxis: {
      // Poucos anéis: os rótulos ficam sobre o eixo N e se sobrepõem no centro.
      splitNumber: 2,
      axisLabel: { color: CHART_COLORS.textSecondary, fontSize: 9, formatter: "{value}%" },
      splitLine,
      axisLine: { show: false },
    },
    series: [
      {
        type: "bar",
        coordinateSystem: "polar",
        itemStyle: {
          color: CHART_COLORS.series1,
          borderColor: CHART_COLORS.surface,
          borderWidth: 2,
        },
        data: ordered.map((s) => s.frequency_percentage),
      },
    ],
  };
}

/** Índice de vento por ano; 1 = ano na média histórica. */
export function interannualOption(
  points: Array<{ year: number; index: number }>
): EChartsCoreOption {
  return {
    grid: { left: 44, right: 16, top: 24, bottom: 24 },
    xAxis: categoryAxis(points.map((p) => String(p.year))),
    yAxis: valueAxis("Índice", {
      min: (v: { min: number }) => Math.min(0.85, Math.floor(v.min * 20) / 20),
      max: (v: { max: number }) => Math.max(1.15, Math.ceil(v.max * 20) / 20),
    }),
    series: [
      {
        type: "bar",
        barWidth: 18,
        itemStyle: { color: CHART_COLORS.series1, borderRadius: BAR_RADIUS },
        data: points.map((p) => p.index),
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: { color: CHART_COLORS.textPrimary, type: "dashed", width: 1.5 },
          label: {
            color: CHART_COLORS.textPrimary,
            fontSize: 10,
            position: "insideEndTop",
            formatter: "média histórica",
          },
          data: [{ yAxis: 1 }],
        },
      },
    ],
  };
}

/** Uso do solo: % da área por classe, em barras horizontais. */
export function landUseOption(
  classes: Array<{ label: string; percentage: number }>
): EChartsCoreOption {
  const ordered = [...classes].sort((a, b) => b.percentage - a.percentage);
  return {
    grid: { left: 72, right: 48, top: 8, bottom: 36 },
    xAxis: valueAxis("% da área", BOTTOM_AXIS_NAME),
    yAxis: categoryAxis(
      ordered.map((c) => c.label),
      { inverse: true }
    ),
    series: [
      {
        type: "bar",
        barWidth: 10,
        itemStyle: { color: CHART_COLORS.series1, borderRadius: BAR_RADIUS_HORIZONTAL },
        label: {
          show: true,
          position: "right",
          color: CHART_COLORS.textPrimary,
          fontSize: 10,
          formatter: (p: { value: number }) => `${nf(p.value)}%`,
        },
        data: ordered.map((c) => c.percentage),
      },
    ],
  };
}

/**
 * Barras agrupadas do comparativo para UM indicador (um eixo só): Área A,
 * Área B e Pernambuco. Cada indicador de unidade diferente vira seu próprio
 * gráfico.
 */
export function comparisonOption(
  metricLabel: string,
  unit: string,
  values: Array<{ name: string; value: number | null }>,
  format: (v: number) => string
): EChartsCoreOption {
  const colors = [CHART_COLORS.series1, CHART_COLORS.series2, CHART_COLORS.series3];
  return {
    title: {
      text: unit ? `${metricLabel} (${unit})` : metricLabel,
      left: 0,
      top: 0,
      textStyle: { color: CHART_COLORS.textPrimary, fontSize: 11, fontWeight: "bold" },
    },
    grid: { left: 8, right: 64, top: 26, bottom: 4, containLabel: true },
    xAxis: valueAxis("", { axisLabel: { show: false }, splitLine: { show: false } }),
    yAxis: categoryAxis(
      values.map((v) => v.name),
      { inverse: true, axisLabel: { ...axisLabel, width: 110, overflow: "truncate" } }
    ),
    series: [
      {
        type: "bar",
        barWidth: 12,
        barCategoryGap: "40%",
        label: {
          show: true,
          position: "right",
          color: CHART_COLORS.textPrimary,
          fontSize: 10,
          formatter: (p: { value: number }) => format(p.value),
        },
        data: values.map((v, i) => ({
          value: v.value == null ? "-" : v.value,
          itemStyle: { color: colors[i % colors.length], borderRadius: BAR_RADIUS_HORIZONTAL },
        })),
      },
    ],
  };
}
